<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { addItemToProjectById, addUnresolvedMultiItemToProject, getFirstItemMetadataId, getItemMetadataExtended } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiWrench from '~icons/mdi/wrench'
import SearchField from '@/components/search/SearchField.vue'

const store  = useStore()
const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t } = useI18n()
const { isOperatorOrAbove } = usePermissions()
const filter = ref('')
const typeFilter = ref(null)
const viewPreferenceKey = 'equipment-list-view'
function savedView() {
  try { return localStorage.getItem(viewPreferenceKey) === 'all' ? 'all' : 'category' }
  catch { return 'category' }
}
const listView = ref(savedView())
watch(listView, (value) => {
  try { localStorage.setItem(viewPreferenceKey, value) } catch { /* Storage may be unavailable. */ }
})
function changeListView(value) {
  listView.value = value
  if (value === 'all' && currentCategoryId.value) {
    const query = { ...route.query }
    delete query.category
    router.push({ path: '/items', query })
  }
}

const typeLabel = computed(() => ({
  1: t('itemTypes.unique'),
  2: t('itemTypes.multi'),
  3: t('itemTypes.quantity'),
  4: t('itemTypes.consumable'),
}))

const TYPE_CHIPS = computed(() => [
  { value: null, label: t('items.allTypes') },
  { value: 1,    label: t('itemTypes.unique') },
  { value: 2,    label: t('itemTypes.multi') },
  { value: 3,    label: t('itemTypes.quantity') },
  { value: 4,    label: t('itemTypes.consumable') },
])

onMounted(async () => {
  if (!store.items.length && !store.isLoadingItems) await store.fetchItems()
  store.fetchCategories()
})

// Single watcher on both — Vue batches these into one callback when both
// change in the same tick (e.g. when picking a new location + warehouse).
watch(
  [() => store.currentWarehouse?.id, () => store.currentLocation?.id],
  () => { store.fetchItems(true); store.fetchCategories(true) }
)

// ─── category navigation ──────────────────────────────────────────────────────
const currentCategoryId = computed(() => route.query.category ? String(route.query.category) : null)

const currentCategory = computed(() =>
  currentCategoryId.value
    ? store.categories.find(c => String(c.id) === currentCategoryId.value) ?? null
    : null
)

const sortCategories = (categories) => [...categories].sort((a, b) => a.name.localeCompare(b.name))
const rootCategories = computed(() => sortCategories(store.categories.filter(c => c.parent == null)))

const subcategories = computed(() =>
  currentCategoryId.value
    ? sortCategories(store.categories.filter(c => String(c.parent) === currentCategoryId.value))
    : []
)

const displayedCategories = computed(() => currentCategoryId.value ? subcategories.value : rootCategories.value)

function itemCountForCategory(catId) {
  const cat = store.categories.find(c => String(c.id) === String(catId))
  return cat?.item_count ?? 0
}

function openCategory(cat) {
  router.push({ path: '/items', query: { category: cat.id } })
}

function goBack() {
  if (!currentCategory.value) { router.back(); return }
  if (currentCategory.value.parent) {
    router.push({ path: '/items', query: { category: currentCategory.value.parent } })
  } else {
    router.push('/items')
  }
}

// ─── items list ───────────────────────────────────────────────────────────────
const showBrowser = computed(() => listView.value === 'category' && !filter.value.trim())

const filteredItems = computed(() => {
  let list = store.items
  if (currentCategoryId.value) {
    list = list.filter(m => {
      const catId = typeof m.category === 'object' ? m.category?.id : m.category
      return String(catId) === currentCategoryId.value
    })
  }
  // Root categories lead to their own lists. Keep uncategorized/orphaned items accessible here.
  if (!currentCategoryId.value && showBrowser.value && rootCategories.value.length) {
    list = list.filter(m => {
      const catId = typeof m.category === 'object' ? m.category?.id : m.category
      return !store.categories.some(c => String(c.id) === String(catId))
    })
  }
  if (typeFilter.value !== null) {
    list = list.filter(m => m.item_type === typeFilter.value)
  }
  const q = filter.value.trim().toLowerCase()
  if (!q) return list
  return list.filter(m => {
    const name = (m.name ?? '').toLowerCase()
    const cat  = m.category?.name?.toLowerCase() ?? ''
    return name.includes(q) || cat.includes(q)
  })
})

const navigate = (m, event) => {
  if (event?.target?.closest('button')) return
  router.push(`/item/${m.id}`)
}

// ─── add to project ───────────────────────────────────────────────────────────
const addingId       = ref(null)
const qtyModal       = ref(null)   // item record to add with quantity prompt
const qtyModalVal    = ref('1')
const qtyModalMax    = ref(null)   // max available units (null = unlimited / still loading)
const qtyModalMaxLoading = ref(false)

async function addToProject(m, e) {
  e.stopPropagation()
  if (addingId.value === m.id) return

  // Type 2 (Multi-item): schedule an unresolved slot — prompt for quantity
  // Type 3 (Quantity): also prompt for quantity
  // Type 4 (Consumable): prompt for quantity, show available stock
  if (m.item_type === 2 || m.item_type === 3 || m.item_type === 4) {
    qtyModal.value    = m
    qtyModalVal.value = '1'
    qtyModalMax.value = null
    qtyModalMaxLoading.value = true
    const project = store.addToProjectProject
    const dateParams = {}
    if (project?.start_time) dateParams.available_from = project.start_time
    if (project?.end_time)   dateParams.available_to   = project.end_time
    if (project?.id)         dateParams.exclude_project = project.id
    try {
      if (m.item_type === 3 || m.item_type === 4) {
        // Quantity/Consumable type — available_quantity or consumable_net_available on the single instance
        const res = await getItemMetadataExtended(0, 0, 0, { item: m.id, page_size: 1, ...dateParams })
        const ext = res.results?.[0]
        qtyModalMax.value = m.item_type === 4
          ? (Number(ext?.consumable_net_available ?? ext?.available_quantity ?? 0))
          : (ext?.available_quantity ?? null)
      } else {
        // Multi-item — count of available physical instances
        const res = await getItemMetadataExtended(0, 0, 0, { item: m.id, available: true, page_size: 1, ...dateParams })
        qtyModalMax.value = res.count ?? (res.results?.length ?? null)
      }
    } catch {
      // If lookup fails, don't block — just leave max unconstrained
    } finally {
      qtyModalMaxLoading.value = false
    }
    return
  }

  // Type 1 (Unique): look up the one instance and add it
  addingId.value = m.id
  try {
    const metaId = await getFirstItemMetadataId(m.id)
    if (!metaId) { toast.error(t('items.noInstanceFound', { name: m.name })); return }
    const sid = store.addToProjectProject?.section_id ?? null
    const res = await addItemToProjectById(store.addToProjectProject.id, metaId, { section_id: sid })
    toast.success(t('items.added', { name: m.name, entity: store.addToProjectProject?.name }))
    if (res?.conflicts?.length) {
      toast.warning(t('items.conflictWarning', { projects: res.conflicts.map(c => c.project_name).join(', ') }))
    }
  } catch (err) {
    toast.error(err?.data?.error ?? err?.message ?? t('items.failedToAdd'))
  } finally {
    addingId.value = null
  }
}

async function confirmQtyModal() {
  const m   = qtyModal.value
  const qty = parseInt(qtyModalVal.value, 10)
  if (!m || isNaN(qty) || qty < 1) return
  if (qtyModalMax.value !== null && qty > qtyModalMax.value) {
    toast.error(t('items.onlyAvailable', { max: qtyModalMax.value, qty }))
    return
  }
  qtyModal.value = null
  addingId.value = m.id
  try {
    let res
    const sid = store.addToProjectProject?.section_id ?? null
    if (m.item_type === 2) {
      // Unresolved slot — no specific instance committed yet
      res = await addUnresolvedMultiItemToProject(store.addToProjectProject.id, m.id, qty, { section_id: sid })
    } else {
      // Type 3 (Quantity) and Type 4 (Consumable): find the one instance and add with quantity
      const metaId = await getFirstItemMetadataId(m.id)
      if (!metaId) { toast.error(t('items.noInstanceFound', { name: m.name })); return }
      res = await addItemToProjectById(store.addToProjectProject.id, metaId, { quantity: qty, section_id: sid })
    }
    toast.success(t('items.addedQty', { qty, name: m.name, entity: store.addToProjectProject?.name }))
    if (res?.conflicts?.length) {
      toast.warning(t('items.conflictWarning', { projects: res.conflicts.map(c => c.project_name).join(', ') }))
    }
  } catch (err) {
    toast.error(err?.data?.error ?? err?.message ?? t('items.failedToAdd'))
  } finally {
    addingId.value = null
  }
}

async function addItemToCheckout(m) {
  try {
    if (m.item_type === 1) {
      const metaId = await getFirstItemMetadataId(m.id)
      if (!metaId) { toast.error(t('items.noInstanceFound', { name: m.name })); return }
      store.addCheckoutItem({ id: metaId, item: { name: m.name, item_type: m.item_type } })
    } else {
      const metaId = await getFirstItemMetadataId(m.id)
      if (!metaId) { toast.error(t('items.noInstanceFound', { name: m.name })); return }
      store.addCheckoutItem({ id: metaId, item: { name: m.name, item_type: m.item_type }, qty: 1 })
    }
    toast.success(t('items.addedToCheckout', { name: m.name }))
  } catch {
    toast.error(t('items.failedToLookup'))
  }
}
</script>

<template>
  <div class="page-view">

    <BasePageHeader :title="currentCategory?.name ?? $t('items.title')" @back="goBack">
      <template #actions>
        <router-link v-if="isOperatorOrAbove" to="/bulk-edit/items" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <router-link v-if="isOperatorOrAbove" to="/bulk-edit/instances" class="btn ghost small">{{ $t('bulkEdit.instances') }}</router-link>
        <button v-if="isOperatorOrAbove" class="btn primary small" @click="router.push('/item/new')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Search -->
    <div class="px-4 pt-3.5 pb-2">
      <SearchField v-model="filter" :debounce="0" :placeholder="$t('items.searchPlaceholder')" />
    </div>

    <div class="px-4 pb-2 max-w-sm">
      <BaseFormField :label="$t('items.listView')" density="compact">
        <BaseSelect :model-value="listView" @update:model-value="changeListView">
          <option value="category">{{ $t('items.byCategory') }}</option>
          <option value="all">{{ $t('items.allEquipment') }}</option>
        </BaseSelect>
      </BaseFormField>
    </div>

    <!-- Type filter chips — shown when in a category or in search mode -->
    <div v-if="currentCategoryId || !showBrowser" class="flex gap-1.5 px-4 pb-2 overflow-x-auto [scrollbar-width:none] [-webkit-overflow-scrolling:touch]">
      <button
        v-for="chip in TYPE_CHIPS"
        :key="String(chip.value)"
        @click="typeFilter = chip.value"
        :class="['flex-shrink-0 px-3 py-1.5 rounded-full border text-[.8rem] font-medium cursor-pointer transition-all', typeFilter === chip.value ? 'bg-blue-700 border-blue-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-[#273344] hover:border-gray-500 hover:text-gray-200']"
      >{{ chip.label }}</button>
    </div>

    <!-- Add-to-project mode banner -->
    <div v-if="store.addToProjectMode" class="flex items-center gap-2.5 bg-purple-950 border-b border-purple-700 px-4 py-2.5 text-[.85rem] text-purple-200 sticky top-[53px] z-[9]">
      <svg class="w-4 h-4 shrink-0 text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 3h2l.4 2M7 13h10l4-8H5.4"/><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      </svg>
      <span class="flex-1">{{ $t('items.addingTo') }} <strong class="text-violet-300">{{ store.addToProjectProject?.name }}</strong><template v-if="store.addToProjectProject?.section_id"> › <strong class="text-violet-200">{{ store.addToProjectProject?.section_name ?? $t('common.section') }}</strong></template></span>
      <button class="btn primary small" @click="store.disableAddToProject()">{{ $t('common.done') }}</button>
    </div>

    <!-- Add-to-project quantity modal -->
    <div v-if="qtyModal" class="modal-overlay" @click.self="qtyModal = null">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('items.howMany', { name: qtyModal.name }) }}</div>
        <div v-if="qtyModalMaxLoading" class="form-hint">{{ $t('items.checkingAvailability') }}</div>
        <div v-else-if="qtyModalMax !== null" class="form-hint">{{ $t('items.available', { count: qtyModalMax }) }}</div>
        <BaseFormField class="mb-3.5 !mt-0">
          <BaseInput
            v-model="qtyModalVal"
            type="number"
            min="1"
            :max="qtyModalMax ?? undefined"
            :aria-label="$t('items.howMany', { name: qtyModal.name })"
            autofocus
            @keydown.enter="confirmQtyModal"
            @keydown.escape="qtyModal = null"
          />
        </BaseFormField>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="qtyModal = null">{{ $t('common.cancel') }}</button>
          <button
            class="btn primary"
            :disabled="!(parseInt(qtyModalVal) >= 1) || (qtyModalMax !== null && parseInt(qtyModalVal) > qtyModalMax)"
            @click="confirmQtyModal"
          >{{ $t('common.add') }}</button>
        </div>
      </div>
    </div>

    <div v-if="store.isLoadingItems || store.isLoadingCategories" class="state-empty">{{ $t('common.loading') }}</div>

    <template v-else>

      <!-- Category browser, available across all locations. -->
      <template v-if="showBrowser && (currentCategoryId || rootCategories.length)">
        <div class="list-stack">
          <template v-if="displayedCategories.length">
            <div v-if="currentCategoryId" class="section-label">{{ $t('items.subcategoriesLabel') }}</div>
            <div
              v-for="cat in displayedCategories"
              :key="cat.id"
              class="data-card cursor-pointer active:scale-[.985]"
              @click="openCategory(cat)"
            >
              <div class="data-card__main">
                <div class="data-card__name">{{ cat.name }}</div>
                <div class="data-card__meta">
                  <span v-if="itemCountForCategory(cat.id)" class="badge badge-accent">{{ itemCountForCategory(cat.id) }} {{ $t('ui.items') }}</span>
                </div>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 ml-2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
            <div v-if="currentCategoryId || filteredItems.length" class="section-label">{{ $t(currentCategoryId ? 'items.itemsLabel' : 'items.uncategorized') }}</div>
          </template>

          <EmptyState v-if="!filteredItems.length && currentCategoryId" :message="$t('items.noItems')" />
          <template v-else>
            <div
              v-for="m in filteredItems"
              :key="m.id"
              class="data-card cursor-pointer active:scale-[.985]"
              @click="navigate(m, $event)"
            >
              <div class="data-card__main">
                <div class="data-card__name">{{ m.name ?? '#' + m.id }}</div>
                <div class="data-card__meta">
                  <span v-if="m.item_type" class="badge badge-success">{{ typeLabel[m.item_type] }}</span>
                  <span v-for="area in m._areas" :key="area" class="badge badge-muted">{{ area }}</span>
                  <!-- availability -->
                  <template v-if="m.item_type === 1">
                    <span v-if="m._has_downtime" class="badge badge-warning">{{ $t('items.inDowntime') }}</span>
                    <span v-else-if="m._available" class="badge badge-success">{{ $t('instanceStatus.available') }}</span>
                    <span v-else class="badge badge-danger">{{ $t('items.notAvailable') }}</span>
                  </template>
                  <template v-else-if="m.item_type === 2">
                    <span :class="m._available_count > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.multiAvailable', { available: m._available_count, total: m._total_count }) }}</span>
                    <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                  </template>
                  <template v-else-if="m.item_type === 3">
                    <span :class="m._available_quantity > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.inStock', { count: m._available_quantity }) }}</span>
                    <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                  </template>
                  <template v-else-if="m.item_type === 4">
                    <span :class="m._consumable_available > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.available', { count: m._consumable_available }) }}</span>
                    <span v-if="m._restock_threshold !== null && m._consumable_available <= m._restock_threshold" class="badge badge-warning">{{ $t('items.restockNeeded') }}</span>
                  </template>
                  <template v-else>
                    <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                  </template>
                </div>
              </div>
              <div class="data-card__actions">
                <button
                  v-if="store.addToProjectMode && isOperatorOrAbove"
                  class="flex items-center justify-center w-[44px] h-[44px] -m-1.5 rounded-full bg-violet-700 text-white border-none cursor-pointer shrink-0 transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:bg-violet-800"
                  :disabled="addingId === m.id"
                  @click.stop.prevent="addToProject(m, $event)"
                  :title="$t('items.addToProject')"
                >
                  <svg v-if="addingId !== m.id" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  <svg v-else class="w-4 h-4 spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <circle cx="12" cy="12" r="9" stroke-dasharray="56" stroke-dashoffset="20"/>
                  </svg>
                </button>
                <button
                  v-if="!store.addToProjectMode"
                  @click.stop.prevent="addItemToCheckout(m)"
                  class="shrink-0 flex items-center justify-center w-[44px] h-[44px] -m-1.5 rounded-full bg-emerald-800 text-emerald-200 border-none cursor-pointer transition-all hover:bg-emerald-700"
                  :title="$t('items.addToCheckout')"
                  :aria-label="$t('items.addToCheckout')"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                </button>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)]">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            </div>
          </template>
        </div>
      </template>

      <!-- ── Search results (flat list) ─────────────────────────────────────── -->
      <template v-else>
        <EmptyState v-if="!filteredItems.length" :message="$t('items.noMatch')" />
        <div v-else class="list-stack">
          <div
            v-for="m in filteredItems"
            :key="m.id"
            class="data-card cursor-pointer active:scale-[.985]"
            @click="navigate(m, $event)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ m.name ?? '#' + m.id }}</div>
              <div class="data-card__meta">
                <span v-if="m.category?.name" class="badge badge-accent">{{ m.category.name }}</span>
                <span v-if="m.item_type" class="badge badge-success">{{ typeLabel[m.item_type] }}</span>
                <span v-for="area in m._areas" :key="area" class="badge badge-muted">{{ area }}</span>
                <!-- availability -->
                <template v-if="m.item_type === 1">
                  <span v-if="m._has_downtime" class="badge badge-warning">{{ $t('items.inDowntime') }}</span>
                  <span v-else-if="m._available" class="badge badge-success">{{ $t('instanceStatus.available') }}</span>
                  <span v-else class="badge badge-danger">{{ $t('items.notAvailable') }}</span>
                </template>
                <template v-else-if="m.item_type === 2">
                  <span :class="m._available_count > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.multiAvailable', { available: m._available_count, total: m._total_count }) }}</span>
                  <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                </template>
                <template v-else-if="m.item_type === 3">
                  <span :class="m._available_quantity > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.inStock', { count: m._available_quantity }) }}</span>
                  <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                </template>
                <template v-else-if="m.item_type === 4">
                  <span :class="m._consumable_available > 0 ? 'badge badge-success' : 'badge badge-muted'">{{ $t('items.available', { count: m._consumable_available }) }}</span>
                  <span v-if="m._restock_threshold !== null && m._consumable_available <= m._restock_threshold" class="badge badge-warning">{{ $t('items.restockNeeded') }}</span>
                </template>
                <template v-else>
                  <span v-if="m._has_downtime" class="badge badge-warning flex items-center gap-1"><MdiWrench class="w-3 h-3" /> {{ $t('items.inDowntime') }}</span>
                </template>
              </div>
            </div>
            <div class="data-card__actions">
              <button
                v-if="store.addToProjectMode && isOperatorOrAbove"
                class="flex items-center justify-center w-[44px] h-[44px] -m-1.5 rounded-full bg-violet-700 text-white border-none cursor-pointer shrink-0 transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:bg-violet-800"
                :disabled="addingId === m.id"
                @click.stop.prevent="addToProject(m, $event)"
                :title="$t('items.addToProject')"
              >
                <svg v-if="addingId !== m.id" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <svg v-else class="w-4 h-4 spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="12" r="9" stroke-dasharray="56" stroke-dashoffset="20"/>
                </svg>
              </button>
              <button
                v-if="!store.addToProjectMode"
                @click.stop.prevent="addItemToCheckout(m)"
                class="shrink-0 flex items-center justify-center w-[44px] h-[44px] -m-1.5 rounded-full bg-emerald-800 text-emerald-200 border-none cursor-pointer transition-all hover:bg-emerald-700"
                :title="$t('items.addToCheckout')"
                :aria-label="$t('items.addToCheckout')"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
              </button>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)]">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
          </div>
        </div>
      </template>

    </template>

  </div>
</template>

<style>
@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin .7s linear infinite; }
</style>
